import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Plus as AddIcon, Trash2 as DeleteIcon, Search as SearchIcon } from 'lucide-react';
import { Button, Input } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { skillService } from '../../services/skillService';
import { useNotificationStore } from '../../store/notificationStore';
import { useConfirmDialog } from '../common/ConfirmDialog';
import type { AgentSkillAssignmentDto, SkillDto } from '../../types/skill';

interface AgentSkillListProps {
  agentId: string;
  readOnly?: boolean;
  providerId?: string;
}

/**
 * Skills attached to an agent (injected into system prompt on chat completion).
 */
export const AgentSkillList = ({ agentId, readOnly = false, providerId }: AgentSkillListProps) => {
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();
  const [assignments, setAssignments] = useState<AgentSkillAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [skills, setSkills] = useState<SkillDto[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [versionPin, setVersionPin] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAssignments = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await skillService.getAgentSkills(agentId);
      setAssignments(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load agent skills';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [agentId, addNotification]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const loadSkillsForPicker = useCallback(async () => {
    setSkillsLoading(true);
    try {
      const result = await skillService.getSkills({
        pageNumber: 1,
        pageSize: 100,
        searchTerm: search.trim() || undefined,
        isActive: true,
        providerId: providerId || undefined,
      });
      setSkills(result.items);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to load skills', 'error');
    } finally {
      setSkillsLoading(false);
    }
  }, [search, providerId, addNotification]);

  useEffect(() => {
    if (!pickerOpen) return;
    const delay = search.trim() ? 300 : 0;
    const t = setTimeout(() => {
      void loadSkillsForPicker();
    }, delay);
    return () => clearTimeout(t);
  }, [pickerOpen, search, loadSkillsForPicker]);

  const openPicker = () => {
    setSelectedSkillId(null);
    setVersionPin('');
    setSearch('');
    setPickerOpen(true);
  };

  const assignedIds = useMemo(() => new Set(assignments.map((a) => a.skillId)), [assignments]);

  const handleAssign = async () => {
    if (!selectedSkillId) {
      addNotification('Select a skill', 'warning');
      return;
    }
    if (assignedIds.has(selectedSkillId)) {
      addNotification('This skill is already assigned', 'warning');
      return;
    }
    const v = versionPin.trim();
    const versionNumber = v === '' ? null : parseInt(v, 10);
    if (v !== '' && Number.isNaN(versionNumber)) {
      addNotification('Version must be a number or empty for default', 'warning');
      return;
    }
    setSaving(true);
    try {
      await skillService.assignSkillToAgent(agentId, selectedSkillId, versionNumber);
      addNotification('Skill assigned to agent', 'success');
      setPickerOpen(false);
      loadAssignments();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to assign skill', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async (row: AgentSkillAssignmentDto) => {
    const ok = await confirm({
      title: 'Remove skill',
      message: `Remove skill "${row.skillName}" from this agent?`,
      confirmText: 'Remove',
    });
    if (!ok) return;
    try {
      await skillService.unassignSkillFromAgent(agentId, row.id);
      addNotification('Skill removed', 'success');
      loadAssignments();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to remove skill', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSkeleton className="h-7 w-7" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[var(--mui-palette-text-secondary)]">
          Skills are merged into the agent&apos;s system prompt when users chat. No skill IDs are sent on each request.
        </p>
        {!readOnly && (
          <Button type="button" variant="default" onClick={openPicker}>
            <AddIcon size={16} className="mr-1" />
            Attach skill
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4">
          <InlineErrorPanel message={error} action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />
        </div>
      )}

      <div className="border border-[var(--mui-palette-divider)] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--mui-palette-divider)] bg-[var(--mui-palette-action-hover)]">
              <th className="px-3 py-2 text-left">Skill</th>
              <th className="px-3 py-2 text-left">Slug</th>
              <th className="px-3 py-2 text-left">Pinned version</th>
              <th className="px-3 py-2 text-right w-[72px]" />
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-2">
                  <p className="text-sm text-[var(--mui-palette-text-secondary)]">
                    No skills attached. Upload skills on the <strong>Skills</strong> page, then attach one here.
                  </p>
                </td>
              </tr>
            ) : (
              assignments.map((a) => (
                <tr key={a.id} className="border-b border-[var(--mui-palette-divider)] last:border-b-0">
                  <td className="px-3 py-2">{a.skillName}</td>
                  <td className="px-3 py-2">{a.skillSlug}</td>
                  <td className="px-3 py-2">
                    {a.pinnedVersionNumber != null ? `v${a.pinnedVersionNumber}` : 'Default (floating)'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!readOnly && (
                      <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => handleUnassign(a)}>
                        <DeleteIcon size={16} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={pickerOpen} onClose={() => !saving && setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Attach skill to agent</DialogTitle>
        <DialogContent>
          <div className="mb-2 mt-1">
            <Input
              className="w-full"
              placeholder="Search skills…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<SearchIcon size={16} />}
            />
          </div>
          {skillsLoading ? (
            <div className="flex justify-center p-4">
              <LoadingSkeleton className="h-7 w-7" />
            </div>
          ) : (
            <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
              {skills
                .filter((s) => !assignedIds.has(s.id))
                .map((s) => (
                  <ListItemButton
                    key={s.id}
                    selected={selectedSkillId === s.id}
                    onClick={() => setSelectedSkillId(s.id)}
                  >
                    <ListItemText primary={s.name} secondary={s.slug} />
                  </ListItemButton>
                ))}
            </List>
          )}
          <div className="mt-2">
            <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Pin to version (optional)</label>
            <Input
              className="w-full"
              placeholder="Empty = use skill default version"
              value={versionPin}
              onChange={(e) => setVersionPin(e.target.value)}
            />
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]">Enter a version number to pin, or leave empty.</p>
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="ghost" onClick={() => setPickerOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="default" onClick={handleAssign} disabled={saving || !selectedSkillId}>
            Attach
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
